import { useState, useCallback, useEffect, useRef } from 'react';
import { generateMode2, uploadImageFull } from '../../../services/api';
import { refreshWallet } from '../../../hooks/useWallet';
import { getProxyUrl } from '../utils/proxyUrl';
import { canSelectHistoryImage, resolveInitialHistoryIndex } from '../utils/imageHistory';
import type { GenConfig, FlowModel, FlowAnswers, OrganizationType } from '../../../types';
import type { GenerationRestoreRequest } from '../../../contexts/GenContext';

interface UseMode2GenerateParams {
  name: string;
  title?: string | null;
  organizationName?: string | null;
  organizationType: OrganizationType;
  genConfig: GenConfig;
  setGenConfig: React.Dispatch<React.SetStateAction<GenConfig>>;
  flowModel: FlowModel;
  /** 從圖片庫「繼續生成」帶進來的還原請求 */
  restoreRequest?: GenerationRestoreRequest | null;
}

export interface Mode2Result {
  imageUrl: string;
  gcsPath: string;
  fileId?: string;
  /** 該位置的圖片已被使用者刪除，僅保留佔位格 */
  deleted?: boolean;
}

/** 還原時要帶進對話面板的內容 */
export interface Mode2ChatRestore {
  /** 每次還原都遞增，讓對話面板知道要重建 */
  token: number;
  /** 來源對話 id：分支時新對話會以它的訊息開頭 */
  parentChatSessionId?: string;
  messages: { role: 'user' | 'model'; content: string }[];
}

/** 還原狀態的提示資訊（讓使用者知道自己看的不是剛生成的內容） */
export interface Mode2RestoredInfo {
  /** 原本的生成時間 */
  originalCreatedAt: string;
  /** 當初使用的工具包已不存在 */
  toolkitMissing: boolean;
  /** 工具包題目自當初以來被改過 */
  toolkitChanged: boolean;
  /** 對應不到現存題目、未被套用的答案 */
  orphanedAnswers: { questionId: string; value: string | boolean }[];
}

export function useMode2Generate({
  name,
  title,
  organizationName,
  organizationType,
  genConfig,
  setGenConfig,
  flowModel,
  restoreRequest,
}: UseMode2GenerateParams) {
  const identityPayload = { name, title, organizationName, organizationType };
  const [imageHistory, setImageHistory] = useState<Mode2Result[]>([]);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState<number>(-1);
  const [isMode2Generating, setIsMode2Generating] = useState(false);
  const [mode2GenerationFailed, setMode2GenerationFailed] = useState(false);
  const [mode3InitialMessage, setMode3InitialMessage] = useState<string | undefined>(undefined);
  const [mode3InitialImageFile, setMode3InitialImageFile] = useState<File | null>(null);
  /** 點數不足錯誤訊息（來自 server 的 402 error 欄位）；非 null 時顯示對話框 */
  const [pointsExhaustedMessage, setPointsExhaustedMessage] = useState<string | null>(null);
  const dismissPointsExhausted = useCallback(() => setPointsExhaustedMessage(null), []);

  /**
   * 生成流程紀錄的歸屬。
   *   - sessionIdRef：本段流程既有的紀錄，每次生成覆蓋它。
   *   - parentSessionIdRef：從圖片庫還原時的來源紀錄，只在還原後「第一次生成」帶出去，
   *     由後端建立分支；來源紀錄本身永不變動。
   * 用 ref 而非 state：生成流程中不會重新 render 也必須讀到最新值。
   */
  const sessionIdRef = useRef<string | undefined>(undefined);
  const parentSessionIdRef = useRef<string | undefined>(undefined);
  const [restoredInfo, setRestoredInfo] = useState<Mode2RestoredInfo | null>(null);
  /** 目前這段流程的紀錄 id；對話面板要用它把對話連結補寫回紀錄 */
  const [generationSessionId, setGenerationSessionId] = useState<string | undefined>(undefined);
  /** 對話面板回報的對話 id。生成時一併送出，涵蓋「先聊再生成」時紀錄尚未存在的情況 */
  const chatSessionIdRef = useRef<string | undefined>(undefined);
  const [chatRestore, setChatRestore] = useState<Mode2ChatRestore | null>(null);

  const handleChatSessionIdChange = useCallback((chatSessionId: string) => {
    chatSessionIdRef.current = chatSessionId;
  }, []);

  const selectedImage = selectedHistoryIndex >= 0 ? imageHistory[selectedHistoryIndex] ?? null : null;
  // Alias for backward compat
  const mode2Result = selectedImage;

  // 套用還原：圖片清單、順序與預覽中的圖都照當初；此時不寫入任何紀錄
  useEffect(() => {
    if (!restoreRequest) return;
    const { session, focusFileId, originalCreatedAt } = restoreRequest;
    const history: Mode2Result[] = session.images.map(img => ({
      imageUrl: img.imageUrl ?? '',
      gcsPath: img.gcsPath ?? '',
      fileId: img.fileId,
      deleted: img.deleted,
    }));
    setImageHistory(history);
    setSelectedHistoryIndex(resolveInitialHistoryIndex(history, focusFileId));
    parentSessionIdRef.current = session.id;
    sessionIdRef.current = undefined;
    setGenerationSessionId(undefined);
    // 分支要有自己的對話：清掉目前的對話 id，下一次送出訊息時後端會複製來源訊息開新的
    chatSessionIdRef.current = undefined;
    setChatRestore({
      token: restoreRequest.token,
      parentChatSessionId: session.chatSessionId,
      messages: (session.chatMessages ?? []).map(m => ({ role: m.role, content: m.content })),
    });
    setRestoredInfo({
      originalCreatedAt,
      toolkitMissing: session.toolkitMissing,
      toolkitChanged: session.toolkitChanged,
      orphanedAnswers: session.orphanedAnswers ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreRequest?.token]);

  /** 這次生成要帶的紀錄歸屬參數 */
  const sessionParams = () => ({
    generationSessionId: sessionIdRef.current,
    parentGenerationSessionId: parentSessionIdRef.current,
    chatSessionId: chatSessionIdRef.current,
  });

  const appendResult = (result: Mode2Result & { generationSessionId?: string }) => {
    window.dispatchEvent(new CustomEvent('jodoni:generation:complete'));
    if (result.generationSessionId) {
      sessionIdRef.current = result.generationSessionId;
      setGenerationSessionId(result.generationSessionId);
    }
    // 分支只在第一次生成時建立，之後同一段流程都覆蓋新紀錄
    parentSessionIdRef.current = undefined;
    setImageHistory(prev => {
      const next = [...prev, result];
      setSelectedHistoryIndex(next.length - 1);
      return next;
    });
  };

  const selectHistoryImage = (index: number) => {
    // 已刪除的佔位格不可選取（因此也不可下載），但仍佔著原本的位置
    if (canSelectHistoryImage(imageHistory, index)) {
      setSelectedHistoryIndex(index);
    }
  };

  /** 初次生成：從 flowAnswers 上傳圖片，再呼叫 mode2 API */
  const handleSubmitFlow = async (flowAnswers: FlowAnswers) => {
    setIsMode2Generating(true);
    try {
      // 1. Pre-upload 所有圖片，取得 per-question fileIds
      const imageAnswers: Record<string, string[]> = {};
      for (const [qId, val] of Object.entries(flowAnswers)) {
        if (Array.isArray(val) && val.length > 0 && val[0] instanceof File) {
          const fileIds = await Promise.all(
            (val as File[]).map(f => uploadImageFull(f).then(r => r.fileId))
          );
          imageAnswers[qId] = fileIds;
        }
      }

      // 2. 分離文字答案（排除 File[] 欄位）
      const textAnswers: Record<string, string | boolean> = {};
      for (const [id, val] of Object.entries(flowAnswers)) {
        if (!Array.isArray(val)) textAnswers[id] = val as string | boolean;
      }

      // 3. 同步 genConfig 以供 Step4Result 使用
      setGenConfig(prev => ({
        ...prev,
        answers: textAnswers,
        uploadedAssets: [],
        imageAnswers,
      }));

      // 4. 呼叫生成 API
      const result = await generateMode2({
        ...identityPayload,
        answers: JSON.stringify(textAnswers),
        imageAnswers: JSON.stringify(imageAnswers),
        flowModelId: flowModel.id,
        ...sessionParams(),
      });
      appendResult(result);
      refreshWallet();
    } catch (error: any) {
      if (error.isPointsExhausted) {
        console.error('Mode 2 Generation blocked: points exhausted', error);
        setPointsExhaustedMessage(error.message || '本月點數已用完，請聯繫客服為您調整！');
      } else if (error.isCommunityViolation) {
        console.error('Mode 2 Generation blocked by community guidelines:', error);
        alert('生成失敗，違反社群生成規範。');
      } else {
        console.error('Mode 2 Generation failed:', error);
        alert('生成失敗，請稍後再試。');
      }
    } finally {
      setIsMode2Generating(false);
    }
  };

  /** 重新生成：用 genConfig 格式，可帶 referenceImage */
  const handleMode2GenerateWithConfig = async (overrideParams?: {
    config?: Partial<GenConfig>;
    referenceImage?: File;
  }) => {
    setIsMode2Generating(true);
    const config = { ...genConfig, ...overrideParams?.config };
    try {
      const result = await generateMode2({
        ...identityPayload,
        answers: JSON.stringify(config.answers),
        imageAnswers: JSON.stringify(config.imageAnswers ?? {}),
        flowModelId: flowModel.id,
        // referenceImage: overrideParams?.referenceImage,
        additionalRequirements: config.additionalRequirements,
        ...sessionParams(),
      });
      appendResult(result);
      refreshWallet();
    } catch (error: any) {
      if (error.isPointsExhausted) {
        console.error('Mode 2 Generation blocked: points exhausted', error);
        setPointsExhaustedMessage(error.message || '本月點數已用完，請聯繫客服為您調整！');
      } else if (error.isCommunityViolation) {
        console.error('Mode 2 Generation blocked by community guidelines:', error);
        alert('生成失敗，違反社群生成規範。');
      } else {
        console.error('Mode 2 Generation failed:', error);
        alert('生成失敗，請稍後再試。');
      }
    } finally {
      setIsMode2Generating(false);
    }
  };

  const handleRegenerateWithConfig = async (updates: Partial<GenConfig>) => {
    const updatedConfig = { ...genConfig, ...updates };
    setGenConfig(updatedConfig);
    await handleMode2GenerateWithConfig({ config: updatedConfig });
  };

  const handleRegenerateWithImage = async (imageFile: File, updates: Partial<GenConfig>) => {
    const updatedConfig = { ...genConfig, ...updates };
    setGenConfig(updatedConfig);
    await handleMode2GenerateWithConfig({ config: updatedConfig, referenceImage: imageFile });
  };

  /** 重新生成：把當前選取圖片的 fileId 當作 referenceImageFileId，並套用 updates */
  const handleRegenerateWithCurrentImage = async (updates: Partial<GenConfig>) => {
    const current = selectedImage;
    const updatedConfig = { ...genConfig, ...updates };
    setGenConfig(updatedConfig);
    setIsMode2Generating(true);
    setMode2GenerationFailed(false);
    try {
      const result = await generateMode2({
        ...identityPayload,
        answers: JSON.stringify(updatedConfig.answers),
        imageAnswers: JSON.stringify(updatedConfig.imageAnswers ?? {}),
        flowModelId: flowModel.id,
        referenceImageFileId: current?.fileId ? String(current.fileId) : undefined,
        additionalRequirements: updatedConfig.additionalRequirements,
        ...sessionParams(),
      });
      appendResult(result);
      refreshWallet();
    } catch (error: any) {
      if (error.isPointsExhausted) {
        console.error('Mode 2 Generation blocked: points exhausted', error);
        setPointsExhaustedMessage(error.message || '本月點數已用完，請聯繫客服為您調整！');
        setMode2GenerationFailed(true);
      } else if (error.isCommunityViolation) {
        console.error('Mode 2 Generation blocked by community guidelines:', error);
        setMode2GenerationFailed(true);
      } else {
        console.error('Mode 2 Generation failed:', error);
        setMode2GenerationFailed(true);
      }
    } finally {
      setIsMode2Generating(false);
    }
  };

  const handleContinueToMode3 = async (feedbackText: string) => {
    if (!mode2Result) return;
    let imageFile: File | null = null;
    try {
      const response = await fetch(getProxyUrl(mode2Result.imageUrl));
      const blob = await response.blob();
      imageFile = new File([blob], 'mode2-result.png', { type: blob.type });
    } catch (err) {
      console.error('Failed to fetch mode2 image for continuation:', err);
    }
    setMode3InitialMessage(feedbackText);
    setMode3InitialImageFile(imageFile);
    setGenConfig(prev => ({ ...prev, mode: 3, continuationFeedback: feedbackText }));
  };

  const resetMode2 = () => {
    setImageHistory([]);
    setSelectedHistoryIndex(-1);
    sessionIdRef.current = undefined;
    parentSessionIdRef.current = undefined;
    chatSessionIdRef.current = undefined;
    setGenerationSessionId(undefined);
    setChatRestore(null);
    setRestoredInfo(null);
    setMode3InitialMessage(undefined);
    setMode3InitialImageFile(null);
    setGenConfig(prev => ({ ...prev, mode: undefined, continuationFeedback: undefined }));
  };

  return {
    mode2Result,
    imageHistory,
    selectedHistoryIndex,
    selectHistoryImage,
    isMode2Generating,
    mode2GenerationFailed,
    mode3InitialMessage,
    mode3InitialImageFile,
    handleSubmitFlow,
    handleRegenerateWithConfig,
    handleRegenerateWithImage,
    handleRegenerateWithCurrentImage,
    handleContinueToMode3,
    resetMode2,
    restoredInfo,
    generationSessionId,
    chatRestore,
    handleChatSessionIdChange,
    pointsExhaustedMessage,
    dismissPointsExhausted,
  };
}
